import React, { useEffect, useRef, useState } from "react"
import "./Tracker.css"
import { Box, Fade, FormControlLabel, FormGroup, Popper, Switch, Typography } from "@mui/material"
import { MdTimer } from "react-icons/md"
import moment from "moment/moment"
import { Toast } from "primereact/toast"
import { getAgentProjectsRequest, getAgentWeeklyReport } from "../../services/wisdomServices/projectService"
import { getSessionItem, setSessionItem } from "../../utils/helper/session"
import { Button } from "primereact/button"
import { getWebTrackerStatus, logTrackerStatusRequest, startWebTrackerRequest, stopWebTrackerRequest, updateLogTrackerStatusRequest } from "../../services/trackerServices/trackerService"

/**
 * @function Tracker
 * @returns {JSX.Element} tracker UI component
 */
export default function Tracker(){
    const [projects, setProjects] = useState([])
    const [project, setProject] = useState(getSessionItem("Tracker").project)
    const toast = useRef(null)
    const [open, setOpen] = useState(false);
    const handleClose = () => setOpen(false);
    const [weeklyTrackedTime, setWeeklyTrackedTime] = useState(getSessionItem("Tracker").weeklyTime)
    const [dailyTrackedTime, setDailyTrackedTime] = useState(getSessionItem("Tracker").dailyTime)
    const [checked, setChecked] = useState(getSessionItem("Tracker").online)
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [lastUpdated, setLastUpdated] = useState("");
    let user = getSessionItem("User")
    let trackerDetail = getSessionItem("Tracker")

    const formatDate = (date) => {
        if (!date) return "";

        return date.replace("T", " ").slice(0, 16);
    };

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
      setOpen((previousOpen) => !previousOpen);
    };

    const canBeOpen = open && Boolean(anchorEl);
    const id = canBeOpen ? 'transition-popper' : undefined;

    const getUserProjects = async() => {
        let response = await getAgentProjectsRequest(user.id, false, false, 1, 100)
        setProjects(response.data)
    }

    const showInfo = (text) => {
        toast.current.show({severity:'info', summary: 'Info', detail:text, life: 3000});
    }

    /**
     * @async
     * @function getWeeklyTrackedTime
     * @param {number} userID 
     * @param {string|number} projectID 
     * @description - fetches weekly and daily tracked time for a user and project
     */
    const getWeeklyTrackedTime = async(userID, projectID) => {
        try {
            const response = await getAgentWeeklyReport(projectID, userID)
            trackerDetail.weeklyTime = response.weeklyTime
            trackerDetail.dailyTime = response.dayTime
            setSessionItem("Tracker", trackerDetail)
            setWeeklyTrackedTime(response.weeklyTime)
            setDailyTrackedTime(response.dayTime)
            if(checked) {
                const webTrackerStatus = await getWebTrackerStatus(user.email);
                setLastUpdated(webTrackerStatus?.lastRunTime)
            }

        } catch (error) {
            console.error("Something went wrong: ", error)
        }
    }
    /**
     * @async
     * @function getWeeklyTrackedTimeUpdated
     * @param {number} userID 
     * @param {string|number} projectID 
     * @description - updates only the daily tracked time
     */
    const getWeeklyTrackedTimeUpdated = async(userID, projectID) => {
        try {
            let response = await getAgentWeeklyReport(projectID, userID)
            trackerDetail.dailyTime = response.dayTime
            
            setSessionItem("Tracker", trackerDetail)
            setDailyTrackedTime(response.dayTime)
            if(checked){
                const webTrackerStatus = await getWebTrackerStatus(user.email);
                setLastUpdated(webTrackerStatus?.lastRunTime)
            }
            
        } catch (error) {
            console.error("Something went wrong: ", error)
        }
    }

    /**
     * @async
     * @function handleProjectSelection
     * @param {string|number} e 
     * @description - handles project selection changes
     */
    const handleProjectSelection = async(e) => {
        if(e.target.value !='default'){
            let selectedProject = e.target.value
            trackerDetail.project = e.target.value
            setSessionItem("Tracker", trackerDetail)
            await getWeeklyTrackedTime(user.id ,e.target.value)
            setProject(selectedProject)

        }else{
            trackerDetail.weeklyTime = 0
            trackerDetail.dailyTime = 0
            trackerDetail.project= "default"
            setSessionItem("Tracker", trackerDetail)
            setWeeklyTrackedTime(0)
            setDailyTrackedTime(0)
            setProject("default")
        }
    }

    /**
     * @async
     * @function handleChange
     * @param {Event} event 
     * @description - handles tracker on/off toggle
     */
    const handleChange = async(event) => {
        trackerDetail.online = event.target.checked
        setSessionItem("Tracker", trackerDetail)
        setSessionItem("StartTime", moment().format('YYYY-MM-DD HH:mm:ss'))
        setChecked(event.target.checked)
        if(event.target.checked){
            const location = getSessionItem("location")
            let keys = {
                username: user.email,
                userID: user.id,
                projectID: Number.parseInt(project),
                startTime: moment().toISOString(),
                latitude: location ? location.latitude.toString() : null,
                longitude: location ? location.longitude.toString() : null
            }
            await startWebTrackerRequest(keys)
            const session = await logTrackerStatusRequest(user.id)
            setSessionItem("session", session.data)
            showInfo("Web tracker is now online")
        }else{
            let keys = {
                username: user.email
            }

            await stopWebTrackerRequest(keys)
            const session = getSessionItem("session")
            await updateLogTrackerStatusRequest(Number.parseInt(session.ID), false)
            showInfo("Web tracker is now offline")
        }
    }

    useEffect(() => {
        if(!checked){
            getUserProjects()
        }
        if(user && project !='default' && open){
            getWeeklyTrackedTimeUpdated(user.id, project)
        }
    }, [open])

    return (
        <div className="tracker-component">
            <Toast ref={toast} position="top-center"/>
            <Button label="Web Tracker" icon="pi pi-clock" onClick={handleClick} className="trackerbutton"/>
        <Popper id={id} open={open} anchorEl={anchorEl} transition className="tracker-popper">
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper' }} className="tracker-popper-header">
                <Typography variant="h4" component="h4"> 
                    {user.firstName} {user.lastname}
                </Typography>
                <Typography id="modal-modal-title" variant="p" component="p">
                    {user.jobDescription}
                </Typography>
                <Box id="modal-description" sx={{ mt: 2 }} className="tracker-popper-content">
                    <div className="tracker-content">
                        <div className="tracker-weekly-detail">
                            <p>This week</p>
                            <p>{weeklyTrackedTime} <MdTimer /></p>
                        </div>
                        <div className="tracker-daily-detail">
                            <p>Current Session</p>
                            <p>{dailyTrackedTime} <MdTimer /></p>
                        </div>
                        <div className="tracker-start-options">
                        <select className="tracker-projects" onChange={handleProjectSelection} defaultValue={project} disabled={checked}>
                            <option value="default">-- Click to select your project --</option>
                            {projects.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                        <div className="modal-controllers">
                            <FormGroup>
                                <FormControlLabel
                                    disabled={project == "default"}
                                    control={
                                                <Switch 
                                                    onClick={handleChange} 
                                                    checked={checked}
                                                />
                                            }
                                    label={checked ? "Online" : "Offline"}
                                />
                            </FormGroup>
                            <Button label="Close" outlined severity="danger" onClick={handleClose} autoFocus/>
                        </div>
                        </div>
                    </div>
                </Box>
                {lastUpdated != '' &&(
                    <Typography variant="p" component="p" className="last-update-label">
                        Last update: <span className="last-update-moment">{formatDate(lastUpdated)}</span>                                                                           
                    </Typography>
                )}
            </Box>
          </Fade>
        )}
        </Popper>
        </div>
    )
}
